import { type RefObject, useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react';

/** The value, but only after it stopped changing for `delayMs` (search boxes, live preview). */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

let sequence = 0;
/** Unique key for client-side rows (React keys only; never sent to the API). */
export function clientKey(prefix = 'row'): string {
  sequence += 1;
  return `${prefix}-${sequence}`;
}

/** True while the media query matches (false where matchMedia is missing, e.g. test DOMs). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      const list = typeof window.matchMedia === 'function' ? window.matchMedia(query) : null;
      list?.addEventListener('change', notify);
      return () => list?.removeEventListener('change', notify);
    },
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false),
    () => false,
  );
}

/** Below 64rem: the register's find panel becomes a find bar and the editor rail a summary line. */
export const NARROW_QUERY = '(max-width: 64rem)';
/** Below 40rem: phones (bottom sheets, stacked cards, docked bars). */
export const PHONE_QUERY = '(max-width: 40rem)';

/**
 * Publishes the height of a bar that stays at the bottom of the viewport (DESIGN_SYSTEM §4, Sticky
 * layers) on the root element:
 * - `--bottom-bar-size` while it is sticky or fixed: `scroll-padding-block-end` (global.css) uses
 *   it, so focus and scroll-into-view stop above the bar and a field is never left under it;
 * - `--docked-size` while it is fixed (docked on phones): the page pads its end by it, and the undo
 *   bar and toasts sit above it, so only one bottom layer shows at a time.
 */
export function useBottomLayer(ref: RefObject<HTMLElement | null>, active = true) {
  useLayoutEffect(() => {
    const layer = ref.current;
    if (!active || !layer) return undefined;
    const root = document.documentElement;
    const set = (name: string, on: boolean) => {
      if (on) root.style.setProperty(name, `${layer.offsetHeight}px`);
      else root.style.removeProperty(name);
    };
    const publish = () => {
      const { position } = getComputedStyle(layer);
      set('--bottom-bar-size', position === 'sticky' || position === 'fixed');
      set('--docked-size', position === 'fixed');
    };
    publish();
    const media = typeof window.matchMedia === 'function' ? window.matchMedia(PHONE_QUERY) : null;
    media?.addEventListener('change', publish);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    observer?.observe(layer);
    return () => {
      media?.removeEventListener('change', publish);
      observer?.disconnect();
      root.style.removeProperty('--bottom-bar-size');
      root.style.removeProperty('--docked-size');
    };
  }, [ref, active]);
}
